/* TanStack Query hooks over the COA custom-attribute admin routes
   (/v1/expense/coa). Attributes are per-entity; a required attribute is enforced by
   the server-side validation gate before a line can post. LIST-kind attributes carry
   a managed set of allowed values. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseFetch } from '@/expense/lib/expense-api';

export type CoaAttributeKind = 'TEXT' | 'LIST';

export interface CoaAttribute {
  attributeId: number;
  entityId: number;
  key: string;
  label: string;
  kind: CoaAttributeKind;
  required: boolean;
  sortOrder: number;
  active: boolean;
}

export interface CoaAttributeValue {
  valueId: number;
  attributeId: number;
  entityId: number;
  value: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

const keys = {
  attributes: () => ['expense', 'coa', 'attributes'] as const,
  values: (attributeId: number) =>
    ['expense', 'coa', 'attributes', attributeId, 'values'] as const,
};

export function useCoaAttributes() {
  return useQuery({
    queryKey: keys.attributes(),
    queryFn: () => expenseFetch<CoaAttribute[]>('/v1/expense/coa/attributes'),
  });
}

export function useCreateAttribute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: {
      key: string;
      label: string;
      kind: CoaAttributeKind;
      required: boolean;
      sortOrder?: number;
    }) =>
      expenseFetch<CoaAttribute>('/v1/expense/coa/attributes', {
        method: 'POST',
        body: JSON.stringify({ sortOrder: 0, ...req }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.attributes() }),
  });
}

export function useAttributeValues(attributeId: number | null) {
  return useQuery({
    queryKey: attributeId
      ? keys.values(attributeId)
      : ['expense', 'coa', 'attributes', 'none', 'values'],
    enabled: attributeId != null,
    queryFn: () =>
      expenseFetch<CoaAttributeValue[]>(
        `/v1/expense/coa/attributes/${attributeId}/values`,
      ),
  });
}

export function useAddAttributeValue(attributeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { value: string; label: string; sortOrder?: number }) =>
      expenseFetch<CoaAttributeValue>(
        `/v1/expense/coa/attributes/${attributeId}/values`,
        { method: 'POST', body: JSON.stringify({ sortOrder: 0, ...req }) },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.values(attributeId) }),
  });
}
